import { useMutation } from '@apollo/client/react';
import { millisecondsInSecond } from 'date-fns/constants';
import { debounce, isEmpty, isEqual, merge, snakeCase } from 'lodash-es';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { CandidateActions } from '@pflegia/types/common/enums/post-back/candidate-actions.enum';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { cookieStorage } from '@pflegia/ui/storage/cookie-storage';
import { useUtmTrackingStore } from '@pflegia/ui/stores/utm-tracking.store';

import { segmentService } from '../application/analytics/segment.service';
import { SEND_POST_BACK_MUTATION } from '../graphql/operations/send-post-back';

export const EXTRA_UTM_PAYLOAD_KEY = 'extra-utm-payload';
const TRACKING_PARAM_REGEX = /^(utm_.*|clid|gclid|fbclid|msclkid|ttclid|gbraid|wbraid)$/i;

const isTrackingParam = (key: string): boolean => {
  const normalizedKey = snakeCase(key);
  return key !== EXTRA_UTM_PAYLOAD_KEY && TRACKING_PARAM_REGEX.test(normalizedKey);
};

const filterTrackingPayload = (payload: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(payload)
      .map(([key, value]) => [snakeCase(key), value] as const)
      .filter(([key, value]) => value && isTrackingParam(key)),
  );

const getUtmValuesFromCookieStorage = (): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};
  for (let i = 0; i < cookieStorage.length; i++) {
    const key = cookieStorage.key(i);
    if (key && key !== EXTRA_UTM_PAYLOAD_KEY) {
      payload[snakeCase(key)] = cookieStorage.getItem(key);
    }
  }
  return payload;
};

const parseCookieEntry = (cookieKey: string) => {
  const raw = cookieStorage.getItem(cookieKey);
  try {
    return JSON.parse(raw as string);
  } catch {
    return raw;
  }
};

const parseHashParams = (): Record<string, string> => {
  if (typeof globalThis.window === 'undefined') return {};

  const rawHash = globalThis.window.location.hash.slice(1);
  if (!rawHash) return {};

  const query = rawHash.includes('?') ? rawHash.split('?', 2)[1] : rawHash;
  return Object.fromEntries(new URLSearchParams(query).entries());
};

const normalizeEntries = (entries: [string, string][]): Record<string, string> =>
  Object.fromEntries(entries.map(([key, val]) => [snakeCase(key), val.replaceAll('/', '')]));

/**
 * Builds the postback payload by merging all param sources.
 *
 * Precedence (highest to lowest): hash > query > UTM cookies > extra-utm cookie
 */
const buildUtmPayload = (queryEntries: [string, string][], hashEntries: [string, string][]): Record<string, unknown> => {
  const parsedExtra = parseCookieEntry(EXTRA_UTM_PAYLOAD_KEY) as Record<string, unknown> | null;
  const cookiesUtm = getUtmValuesFromCookieStorage();

  const merged = merge({}, parsedExtra ?? {}, cookiesUtm, normalizeEntries(queryEntries), normalizeEntries(hashEntries));

  return filterTrackingPayload(merged);
};

const persistUtmPayload = (payload: Record<string, unknown>) => {
  if (isEmpty(payload) || isEqual(parseCookieEntry(EXTRA_UTM_PAYLOAD_KEY), payload)) return;

  cookieStorage.setItem(EXTRA_UTM_PAYLOAD_KEY, JSON.stringify(payload));
};

export const useTrackUtm = (ignoreAuth = false) => {
  const authenticated = useAuth();
  const searchParams = useSearchParams();
  const [sendPostBackRequest] = useMutation(SEND_POST_BACK_MUTATION);
  const { set, trackedActions } = useUtmTrackingStore();

  const latestRef = useRef({
    authenticated,
    ignoreAuth,
    trackedActions,
    utmPayload: {} as Record<string, unknown>,
    set,
    sendPostBackRequest,
  });

  useEffect(() => {
    latestRef.current.authenticated = authenticated;
    latestRef.current.ignoreAuth = ignoreAuth;
    latestRef.current.trackedActions = trackedActions;
    latestRef.current.set = set;
    latestRef.current.sendPostBackRequest = sendPostBackRequest;
  });

  const utmPayload = useMemo(() => buildUtmPayload(Array.from(searchParams.entries()), Object.entries(parseHashParams())), [searchParams]);

  useEffect(() => {
    latestRef.current.utmPayload = utmPayload;
  }, [utmPayload]);

  const debouncedRef = useRef<ReturnType<typeof debounce>>(null);

  useEffect(() => {
    debouncedRef.current = debounce(
      (candidateAction: CandidateActions) => {
        const {
          authenticated: isAuthed,
          ignoreAuth: skip,
          trackedActions: tracked,
          utmPayload: payload,
          set: storeSet,
          sendPostBackRequest: mutate,
        } = latestRef.current;

        const hasConsent = segmentService.userHasGrantedConsent();
        const isBlocked = !hasConsent || (!skip && isAuthed) || isEmpty(payload);

        if (isBlocked || isEqual(tracked[candidateAction], payload)) return;

        persistUtmPayload(payload);
        storeSet(candidateAction, payload);
        mutate({ variables: { utmPayload: payload, action: candidateAction } });
      },
      millisecondsInSecond,
      { leading: true, trailing: false },
    );

    return () => debouncedRef.current?.cancel();
  }, []);

  const trackUtm = useCallback((action: CandidateActions) => debouncedRef.current?.(action), []);

  return {
    trackUtm,
    /**
     * @warning This is exposed for testing purposes only and should not be used directly.
     * It may be removed without warning in the future.
     */
    __debouncedRef: debouncedRef,
  };
};
